// AdminHubPage.js — admin-only hub catalog cleanup at /admin/hub.
//
// Lists every endProducts doc with review and Remove / Restore controls so I
// can approve pending submissions, prune duplicates, QA artifacts, and stale
// variants without touching git or GCS.
// Soft-delete only: flips `removed: true` on the doc — the underlying GCS
// HTML stays intact. Public Hub surfaces hide removed items via
// fetchEndProducts and the direct detail gate.
//
// Search + genre tag let me narrow to one cluster at a time (e.g. all 11
// blackjack variants), pick the keeper, and remove the rest.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const REPORT_CATEGORY_LABELS = {
  copyright:         'Copyright / IP',
  sexually_explicit: 'Sexually explicit',
  violence_or_hate:  'Violence / hate',
  spam_or_scam:      'Spam / scam',
  other:             'Other',
};

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const GENRE_RULES = [
  { tag: 'checkers', re: /\bcheck(?!.*password)/i },
  { tag: 'chess', re: /\bchess\b/i },
  { tag: 'candy-match', re: /candy|match.three|match-three|sweet/i },
  { tag: '2048', re: /\b2048\b|swipe.merge/i },
  { tag: 'flappy', re: /flap/i },
  { tag: 'snake', re: /\bsnake\b/i },
  { tag: 'tetris', re: /tetris/i },
  { tag: 'pacman', re: /pac.?man/i },
  { tag: 'cards', re: /spades|hearts|cribbage|blackjack|poker|solitaire|crazy|euchre|klondike/i },
  { tag: 'word-puzzle', re: /sudoku|crossword|hangman|squaredle|wordle|word.quest/i },
  { tag: 'utility', re: /piano|spirograph|tip.calc|tracker|kanban|notes|pomodoro|password/i },
  { tag: 'arcade', re: /connect.four|battle|breakout|asteroid|adventure|frog|pong|tower|defender|invader|space|centipede|defender|dominos/i },
  { tag: 'qa-junk', re: /\bqa[-_]?\d|-qa\b|dashboard.qa|detail.qa|[0-9a-f]{6,}$|-v[0-9]+$/i },
];

function genreOf(p) {
  const text = `${p.slug || ''} ${p.name || ''} ${p.title || ''}`;
  for (const { tag, re } of GENRE_RULES) {
    if (re.test(text)) return tag;
  }
  return 'other';
}

const RELATION_GENERIC_WORDS = new Set([
  'agent', 'aiharness', 'aiwizard', 'and', 'app', 'built', 'community', 'for',
  'from', 'game', 'games', 'generated', 'harness', 'hub', 'improve', 'inspect',
  'interactive', 'into', 'iterate', 'mini', 'miniapp', 'mchatai', 'or', 'play',
  'product', 'source', 'that', 'the', 'this', 'together', 'version', 'web',
  'with', 'you', 'your',
]);

const RELATION_GENERIC_CONTEXT_IDS = new Set([
  'universal',
  'frontend-skill',
  'mobile-frontend',
  'mobile-web',
  'responsive-frontend',
  'visual-quality',
  'learned',
]);

function relationArray(value) {
  return Array.isArray(value) ? value.filter(Boolean).map(String) : [];
}

function relationLower(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeRelationText(value) {
  return relationLower(value)
    .replace(/\bv?\d+(\.\d+)*\b/g, ' ')
    .replace(/\b(head.to.head|head-to-head|single.player|single-player)\b/g, ' ')
    .replace(/[^\w]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function relationTokens(...parts) {
  const out = new Set();
  parts.flat(Infinity).forEach((part) => {
    relationLower(part)
      .replace(/v?\d+(\.\d+)*/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .split(/\s+/)
      .forEach((token) => {
        if (token.length >= 3 && !RELATION_GENERIC_WORDS.has(token)) out.add(token);
      });
  });
  return out;
}

function relationSignalSet(values, product) {
  const generic = new Set([
    ...RELATION_GENERIC_WORDS,
    relationLower(product.contentCategory || product.type),
    relationLower(product.subType),
  ]);
  return new Set(relationArray(values).map(relationLower).filter((value) => value && !generic.has(value)));
}

function relationSourceSet(product, key, includeGeneric = true) {
  const values = relationArray(product.sourceBundle?.[key]);
  return new Set(includeGeneric ? values : values.filter((id) => !RELATION_GENERIC_CONTEXT_IDS.has(id)));
}

function relationIntersection(a, b) {
  return [...a].filter((value) => b.has(value));
}

function relationDice(a, b) {
  const toBigrams = (value) => {
    const text = normalizeRelationText(value).replace(/\s+/g, '');
    if (text.length < 2) return new Set(text ? [text] : []);
    const out = new Set();
    for (let i = 0; i < text.length - 1; i += 1) out.add(text.slice(i, i + 2));
    return out;
  };
  const aa = toBigrams(a);
  const bb = toBigrams(b);
  if (!aa.size || !bb.size) return 0;
  return (2 * relationIntersection(aa, bb).length) / (aa.size + bb.size);
}

function relationSignals(product) {
  const tags = relationArray(product.tags);
  const topics = relationArray(product.topics);
  const source = product.sourceBundle || {};
  return {
    id: product.id || product.packageId || product._id,
    _id: product._id,
    slug: product.slug || product._id,
    name: product.name || product.title || product.slug || product._id,
    category: relationLower(product.contentCategory || product.type),
    subType: relationLower(product.subType),
    titleKey: normalizeRelationText(product.name || product.title || product.slug || product._id),
    slugKey: normalizeRelationText(product.slug || product._id),
    textTokens: relationTokens(product.name, product.title, product.slug, product.summary, product.description, tags, topics),
    tags: relationSignalSet(tags, product),
    topics: relationSignalSet(topics, product),
    wisdom: relationSourceSet(product, 'wisdomIDs', false),
    templates: relationSourceSet(product, 'templateIDs'),
    assets: relationSourceSet(product, 'assetPackIDs'),
    capabilities: relationSourceSet(product, 'capabilityPackIDs'),
    components: relationSourceSet(product, 'componentIDs'),
    sourceArtifactID: String(source.sourceArtifactID || ''),
    productDNAUUID: String(source.productDNAUUID || ''),
    originalGoal: String(source.originalGoal || product.originalGoal || ''),
    removed: product.removed === true,
  };
}

function relationTypeMeta(type) {
  if (type === 'exact-duplicate') {
    return {
      label: 'Exact Duplicate',
      shortLabel: 'Exact Dup',
      tone: '#7f1d1d',
      bg: '#fef2f2',
      border: '#fecaca',
      description: 'Hard provenance match within the same app type: same product DNA/source artifact plus near-identical title or slug.',
    };
  }
  if (type === 'near-duplicate') {
    return {
      label: 'Near Duplicate',
      shortLabel: 'Near Dup',
      tone: '#b91c1c',
      bg: '#fff7ed',
      border: '#fed7aa',
      description: 'Heuristic match: similar title, slug, template, or source signals. Inspect before removing.',
    };
  }
  if (type === 'branch') {
    return {
      label: 'Branch / Offshoot',
      shortLabel: 'Branch',
      tone: '#166534',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      description: 'Likely branch/offshoot relationship from recovered goal or lineage signals.',
    };
  }
  return {
    label: 'Related',
    shortLabel: 'Related',
    tone: '#374151',
    bg: '#f9fafb',
    border: '#d1d5db',
    description: 'Loose relationship from shared category, tags, words, wisdom, template, assets, or Lego.',
  };
}

function relationCandidateFor(sourceProduct, candidateProduct) {
  const a = relationSignals(sourceProduct);
  const b = relationSignals(candidateProduct);
  if (!a._id || a._id === b._id) return null;
  let score = 0;
  const reasons = [];
  const sameCategory = Boolean(a.category && b.category && a.category === b.category);
  const crossCategory = Boolean(a.category && b.category && a.category !== b.category);

  if (sameCategory) { score += 4; reasons.push('same app type'); }
  else if (crossCategory) { reasons.push(`different app types: ${a.category} vs ${b.category}`); }
  if (a.subType && a.subType === b.subType) { score += 5; reasons.push('same subtype'); }

  const titleSimilarity = relationDice(a.titleKey, b.titleKey);
  if (titleSimilarity >= 0.86) { score += 46; reasons.push(`near-identical title (${titleSimilarity.toFixed(2)})`); }
  else if (titleSimilarity >= 0.68) { score += 24; reasons.push(`similar title (${titleSimilarity.toFixed(2)})`); }

  const slugSimilarity = relationDice(a.slugKey, b.slugKey);
  if (slugSimilarity >= 0.86) { score += 34; reasons.push(`near-identical slug (${slugSimilarity.toFixed(2)})`); }
  else if (slugSimilarity >= 0.68) { score += 16; reasons.push(`similar slug (${slugSimilarity.toFixed(2)})`); }

  const sharedText = relationIntersection(a.textTokens, b.textTokens);
  if (sharedText.length >= 2) { score += Math.min(20, sharedText.length * 5); reasons.push(`shared words: ${sharedText.slice(0, 4).join(', ')}`); }

  const sharedTags = relationIntersection(a.tags, b.tags);
  if (sharedTags.length) { score += Math.min(18, sharedTags.length * 4); reasons.push(`shared tags: ${sharedTags.slice(0, 4).join(', ')}`); }

  const sharedTopics = relationIntersection(a.topics, b.topics);
  if (sharedTopics.length) { score += Math.min(18, sharedTopics.length * 5); reasons.push(`shared topics: ${sharedTopics.slice(0, 4).join(', ')}`); }

  const sharedTemplates = relationIntersection(a.templates, b.templates);
  if (sharedTemplates.length) { score += Math.min(55, sharedTemplates.length * 35); reasons.push(`same template: ${sharedTemplates.slice(0, 3).join(', ')}`); }

  const sharedComponents = relationIntersection(a.components, b.components);
  if (sharedComponents.length) { score += Math.min(40, sharedComponents.length * 10); reasons.push(`shared Lego: ${sharedComponents.slice(0, 4).join(', ')}`); }

  const sharedWisdom = relationIntersection(a.wisdom, b.wisdom);
  if (sharedWisdom.length) { score += Math.min(28, sharedWisdom.length * 7); reasons.push(`shared wisdom: ${sharedWisdom.slice(0, 4).join(', ')}`); }

  const sharedAssets = relationIntersection(a.assets, b.assets);
  if (sharedAssets.length) { score += Math.min(18, sharedAssets.length * 6); reasons.push(`shared assets: ${sharedAssets.slice(0, 3).join(', ')}`); }

  const sharedCaps = relationIntersection(a.capabilities, b.capabilities);
  if (sharedCaps.length) { score += Math.min(24, sharedCaps.length * 8); reasons.push(`shared packs: ${sharedCaps.slice(0, 3).join(', ')}`); }

  const sharedSourceArtifact = Boolean(a.sourceArtifactID && a.sourceArtifactID === b.sourceArtifactID);
  const sharedProductDNA = Boolean(a.productDNAUUID && a.productDNAUUID === b.productDNAUUID);
  const exactIdentity = Boolean(
    (a.titleKey && a.titleKey === b.titleKey)
    || (a.slugKey && a.slugKey === b.slugKey)
    || titleSimilarity >= 0.96
    || slugSimilarity >= 0.96
  );
  const nearIdentity = Boolean(titleSimilarity >= 0.86 || slugSimilarity >= 0.86 || sharedText.length >= 2);

  if (sharedSourceArtifact && exactIdentity) {
    score += 65;
    reasons.push('same source artifact');
  } else if (sharedSourceArtifact && nearIdentity) {
    score += 18;
    reasons.push('same source artifact, similar identity');
  }

  if (sharedProductDNA && exactIdentity) {
    score += 90;
    reasons.push('same product DNA');
  } else if (sharedProductDNA && nearIdentity) {
    score += 24;
    reasons.push('same product DNA, similar identity');
  }

  const branch = relationLower(a.originalGoal).includes('branch')
    && [b.slug, b.name, b.sourceArtifactID].map(relationLower).filter((value) => value.length >= 6).some((value) => relationLower(a.originalGoal).includes(value));
  if (branch) { score += 80; reasons.push('goal says branch/offshoot'); }

  if (score < 28) return null;
  const hardDuplicate = sameCategory && exactIdentity && (sharedSourceArtifact || sharedProductDNA);
  const nearDuplicate = sameCategory && score >= 82 && nearIdentity;
  const relationType = branch
    ? 'branch'
    : hardDuplicate
      ? 'exact-duplicate'
      : nearDuplicate
        ? 'near-duplicate'
      : 'related';
  return { ...candidateProduct, relationType, score, relationReasons: reasons.slice(0, 5) };
}

function relatedCandidatesFor(product, items) {
  return items
    .map((candidate) => relationCandidateFor(product, candidate))
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || (a.name || '').localeCompare(b.name || ''))
    .slice(0, 8);
}

// Phase Hub-UX.3 — inline preview overlay. Right-side drawer (60% width on
// desktop, full-screen on small viewports) so the admin can scrub through
// the list and triage without bouncing into new tabs. Renders differently
// per contentCategory:
//   miniApp / game / website : iframe of the /u/{uid}/{slug}/ play URL
//   prompt (dreamsnap)       : full preview image
//   prompt (audio-library*)  : <audio controls> on audioPreviewURL
//   prompt (dreamsnap-video) : <video controls> on videoPreviewURL (or "no preview")
//   macOSApp                 : info card + Open in new tab
function RepointSection({ product, relationCandidates = [], onRepoint, onRevertRepoint }) {
  // Only playable products have a servable URL to re-point.
  const category = product.contentCategory || 'miniApp';
  const isPlayable = category === 'miniApp' || category === 'game' || category === 'unityGame' || category === 'website';
  const [targetSlug, setTargetSlug] = useState('');
  // Manual-entry targets default to the SAME owner as the source (the common
  // "replace my own game with my newer build" case). Derived (not state) so it
  // can't go stale when the drawer swaps items.
  const ownUid = product.publisherUid || product.publisherShortUid || '';
  const [adopt, setAdopt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const repointed = product.repoint || null;   // present when the parent hydrated it from the project doc

  if (!isPlayable) return null;

  const run = async (fn, okMsg) => {
    setBusy(true); setMsg('');
    try { await fn(); setMsg(okMsg); }
    catch (err) { setMsg('Error: ' + (err?.message || 'failed')); }
    finally { setBusy(false); }
  };

  const apply = (slug, uid) => {
    if (!slug) { setMsg('Pick or type a target slug first.'); return; }
    if (!window.confirm(
      `Re-point "${product.slug}" to serve the bytes of "${slug}"?\n\n` +
      `The URL /u/${(product.publisherShortUid || product.publisherUid || '').slice(0, 8)}/${product.slug}/ stays the same but plays the target build.` +
      (adopt ? '\nThe listing name/description/icon will also adopt the target.' : '') +
      '\n\nReversible from this panel.'
    )) return;
    run(() => onRepoint({ slug, uid: uid || ownUid }, adopt), `Re-pointed → ${slug}. Refreshing…`);
  };

  return (
    <section style={{ padding: 14, background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <strong style={{ color: '#111827', fontSize: 13 }}>Re-point URL</strong>
        <span style={{ color: '#6b7280', fontSize: 11 }}>keep this URL, serve another build</span>
      </div>
      {repointed && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 8, marginBottom: 8, fontSize: 11, color: '#1e40af' }}>
          Currently re-pointed → <strong>{repointed.targetSlug}</strong>
          <button type="button" disabled={busy}
            onClick={() => { if (window.confirm('Revert this URL to its own original bytes + identity?')) run(() => onRevertRepoint(), 'Reverted. Refreshing…'); }}
            style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700, background: '#fff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer' }}>
            Revert
          </button>
        </div>
      )}
      <div style={{ color: '#6b7280', fontSize: 11, lineHeight: 1.35, marginBottom: 8 }}>
        Sets this project's <code>storagePath</code> to the target's bytes (non-destructive, reversible). Shared links + videos keep working; the leaderboard stays on this slug. Visitors with a freshly-cached copy may take a few minutes to see the swap.
      </div>
      {relationCandidates.length > 0 && (
        <div style={{ display: 'grid', gap: 5, marginBottom: 8 }}>
          {relationCandidates.slice(0, 5).map((c) => (
            <div key={c._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 8px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#111827', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || c.title || c.slug}</div>
                <div style={{ color: '#6b7280', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.slug}</div>
              </div>
              <button type="button" disabled={busy || c.slug === product.slug}
                onClick={() => apply(c.slug, c.publisherUid || c.publisherShortUid)}
                style={{ flexShrink: 0, padding: '4px 10px', fontSize: 11, fontWeight: 700, background: '#1f2025', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                Re-point here
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        <input type="text" placeholder="…or target slug" value={targetSlug}
          onChange={(e) => setTargetSlug(e.target.value.trim())}
          style={{ flex: 1, minWidth: 0, padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }} />
        <button type="button" disabled={busy || !targetSlug}
          onClick={() => apply(targetSlug, ownUid)}
          style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: targetSlug ? '#1f2025' : '#e5e7eb', color: targetSlug ? '#fff' : '#9ca3af', border: 'none', borderRadius: 6, cursor: targetSlug ? 'pointer' : 'not-allowed' }}>
          Apply
        </button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
        <input type="checkbox" checked={adopt} onChange={(e) => setAdopt(e.target.checked)} />
        Adopt target's name / description / icon / screenshots
      </label>
      {msg && <div style={{ marginTop: 8, fontSize: 11, color: msg.startsWith('Error') ? '#b91c1c' : '#15803d' }}>{msg}</div>}
    </section>
  );
}

function PreviewModal({ product, index, total, relationCandidates = [], onClose, onPrev, onNext, onToggleRemoved, onSelectCandidate, onRepoint, onRevertRepoint }) {
  useEffect(() => {
    const onKey = (e) => {
      // Ignore while typing in an input/textarea (search box, etc.)
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      if (e.key === 'Escape') { onClose(); return; }
      if ((e.key === 'ArrowDown' || e.key === 'j') && onNext) { e.preventDefault(); onNext(); return; }
      if ((e.key === 'ArrowUp' || e.key === 'k') && onPrev) { e.preventDefault(); onPrev(); return; }
    };
    window.addEventListener('keydown', onKey);
    // Body overflow only locks the background page; the drawer + table remain
    // scrollable independently. (Phase Hub-UX.3.1 — was 'hidden' globally
    // but we want the table behind the drawer to keep scrolling.)
    return () => { window.removeEventListener('keydown', onKey); };
  }, [onClose, onPrev, onNext]);

  const url = product._url;
  const category = product.contentCategory || 'miniApp';
  const subType = product.subType || '';
  const isPrompt = category === 'prompt';
  const isPlayable = !isPrompt && url && (category === 'miniApp' || category === 'game' || category === 'unityGame' || category === 'website');
  const isMacOSApp = category === 'macOSApp';
  const slug = product.slug || product._id || '';
  const hubURL = slug ? `https://mchatai.com/hub/${slug}` : '';
  const playURL = slug && !isPrompt && !isMacOSApp ? `https://mchatai.com/hub/${slug}` : '';
  const selectedURLs = [
    url && { label: 'Open /u URL', value: url },
    hubURL && { label: 'Open Hub Page', value: hubURL },
    playURL && { label: 'Open Play Page', value: playURL },
  ].filter(Boolean);

  return (
    // Non-modal drawer (Phase Hub-UX.3.1) — pinned to the right edge but
    // doesn't dim the page, so the admin can keep clicking rows in the
    // visible table behind it to swap previews without closing first.
    <div
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0,
               width: 'min(560px, 45vw)', background: '#fff',
               boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.15)',
               borderLeft: '1px solid #e5e7eb',
               display: 'flex', flexDirection: 'column', zIndex: 50 }}>
        <header style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
                          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {product.name || product.title || '(untitled)'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {category}{subType && ` · ${subType}`} · {product.publisherName || product.publisherUid?.slice(0, 8) + '…'}
              {typeof index === 'number' && typeof total === 'number' && (
                <span style={{ marginLeft: 8, color: '#9ca3af' }}>· {index + 1} / {total}</span>
              )}
            </div>
          </div>
          {/* Prev / Next — keyboard shortcuts: ↑/k (prev), ↓/j (next). */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={onPrev} disabled={!onPrev}
              title="Previous item (↑ or k)"
              style={{ padding: '6px 10px', fontSize: 13, background: onPrev ? '#fff' : '#f3f4f6',
                       color: onPrev ? '#374151' : '#9ca3af',
                       border: '1px solid #e5e7eb', borderRadius: 6,
                       cursor: onPrev ? 'pointer' : 'not-allowed' }}>↑</button>
            <button onClick={onNext} disabled={!onNext}
              title="Next item (↓ or j)"
              style={{ padding: '6px 10px', fontSize: 13, background: onNext ? '#fff' : '#f3f4f6',
                       color: onNext ? '#374151' : '#9ca3af',
                       border: '1px solid #e5e7eb', borderRadius: 6,
                       cursor: onNext ? 'pointer' : 'not-allowed' }}>↓</button>
          </div>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Open ↗
            </a>
          )}
          <button onClick={onToggleRemoved}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                     background: product.removed ? '#16a34a' : '#fee2e2',
                     color: product.removed ? '#fff' : '#991b1b',
                     border: '1px solid ' + (product.removed ? '#16a34a' : '#fecaca'),
                     borderRadius: 6, cursor: 'pointer' }}>
            {product.removed ? 'Restore to Hub' : 'Remove'}
          </button>
          <button onClick={onClose}
            title="Close (Esc)"
            style={{ padding: '6px 12px', fontSize: 12, background: '#fff',
                     color: '#374151', border: '1px solid #e5e7eb',
                     borderRadius: 6, cursor: 'pointer' }}>
            ✕
          </button>
        </header>
        <div style={{ flex: 1, overflow: 'auto', background: '#f9fafb', padding: 0 }}>
          <section style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            padding: 14,
            background: '#fff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
          }}>
            <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 900, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>
              Selected item
            </div>
            <div style={{ color: '#111827', fontSize: 14, fontWeight: 900, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {product.name || product.title || '(untitled)'}
            </div>
            <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3, overflowWrap: 'anywhere' }}>
              {slug}
            </div>
            {product.removed && (
              <div style={{ display: 'inline-block', marginTop: 6, padding: '2px 7px', borderRadius: 999, background: '#fef2f2', color: '#991b1b', fontSize: 10, fontWeight: 900 }}>
                HIDDEN FROM HUB
              </div>
            )}
            {selectedURLs.length > 0 && (
              <div style={{ display: 'grid', gap: 5, marginTop: 10 }}>
                {selectedURLs.map((item) => (
                  <div key={item.label} style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    gap: 7,
                    alignItems: 'center',
                    color: '#374151',
                    fontSize: 11,
                  }}>
                    <span style={{ color: '#6b7280', fontWeight: 700 }}>{item.label.replace('Open ', '')}</span>
                    <code style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: 5,
                      padding: '3px 5px',
                      color: '#111827',
                    }}>
                      {item.value}
                    </code>
                    <a href={item.value} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#2563eb', fontSize: 11, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      Open ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
          {relationCandidates.length > 0 && (
            <section style={{ padding: 14, background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <strong style={{ color: '#111827', fontSize: 13 }}>Related / Duplicate Candidates</strong>
                <span style={{ color: '#6b7280', fontSize: 11 }}>{relationCandidates.length}</span>
              </div>
              <div style={{ color: '#6b7280', fontSize: 11, lineHeight: 1.35, marginBottom: 8 }}>
                Exact duplicates require matching product DNA/source artifact plus matching title or slug identity. Near duplicates are similarity candidates and should be inspected before removal.
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {relationCandidates.map((candidate) => {
                  const meta = relationTypeMeta(candidate.relationType);
                  return (
                    <button
                      key={candidate._id}
                      type="button"
                      onClick={() => onSelectCandidate?.(candidate)}
                      style={{
                        textAlign: 'left',
                        border: `1px solid ${meta.border}`,
                        borderRadius: 8,
                        background: candidate.removed ? '#fef2f2' : meta.bg,
                        padding: 8,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                        <span style={{ minWidth: 0, color: '#111827', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {candidate.name || candidate.title || candidate.slug}
                        </span>
                        <span style={{
                          color: meta.tone,
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          flexShrink: 0,
                        }}>
                          {meta.label} · {candidate.score}
                        </span>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {candidate.slug || candidate._id}
                      </div>
                      {candidate.relationReasons?.length > 0 && (
                        <div style={{ color: '#4b5563', fontSize: 11, marginTop: 4, lineHeight: 1.35 }}>
                          {candidate.relationReasons.slice(0, 2).join(' · ')}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
          <RepointSection
            key={product._id || product.slug}
            product={product}
            relationCandidates={relationCandidates}
            onRepoint={onRepoint}
            onRevertRepoint={onRevertRepoint}
          />
          {isPlayable && (
            <iframe src={url} title={product.name || product.slug}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: 600 }}
              sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-popups allow-forms" />
          )}
          {isPrompt && subType === 'dreamsnap' && product.iconURL && (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <img src={product.iconURL} alt=""
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8,
                         boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            </div>
          )}
          {isPrompt && (subType === 'audio-library' || subType === 'audio-library-speech') && (
            <div style={{ padding: 24 }}>
              {product.audioPreviewURL ? (
                <audio controls src={product.audioPreviewURL} style={{ width: '100%' }} />
              ) : (
                <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No audio preview generated.</div>
              )}
            </div>
          )}
          {isPrompt && subType === 'dreamsnap-video' && (
            <div style={{ padding: 24 }}>
              {product.videoPreviewURL ? (
                <video controls src={product.videoPreviewURL} style={{ width: '100%', maxHeight: '70vh' }} />
              ) : (
                <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  No video preview yet. (OpenRouter video models not yet available; the
                  iOS gallery shows a "coming soon" placeholder.)
                </div>
              )}
            </div>
          )}
          {isPrompt && (
            <div style={{ padding: '0 24px 24px', borderTop: '1px solid #e5e7eb', marginTop: 12 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 18, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Prompt</h3>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                            padding: 12, fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {product.prompt || product.description || '(empty)'}
              </div>
              {product.modelHint && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                  Model: <code>{product.modelHint}</code>
                </div>
              )}
              {Array.isArray(product.tags) && product.tags.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {product.tags.map((t) => (
                    <span key={t} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999,
                                            background: '#eef2ff', color: '#4338ca' }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMacOSApp && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🖥️</div>
              <div style={{ fontSize: 14, marginBottom: 12 }}>This is a macOS app — no in-browser preview.</div>
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', padding: '10px 20px', background: '#111827', color: '#fff',
                           borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  Open product page ↗
                </a>
              )}
            </div>
          )}
          {!isPlayable && !isPrompt && !isMacOSApp && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: 14, marginBottom: 12 }}>No in-browser preview for content type <code>{category}</code>.</div>
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', padding: '10px 20px', background: '#111827', color: '#fff',
                           borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  Open in new tab ↗
                </a>
              )}
            </div>
          )}
        </div>
      </div>
  );
}

export default function AdminHubPage() {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [removedOnly, setRemovedOnly] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);   // Phase Hub-UX.3 — prompts have their own /admin/prompts page
  const [showRelations, setShowRelations] = useState(true);
  const [error, setError] = useState('');
  // Track preview by productId rather than by reference so filter/sort
  // changes don't dangle the drawer on a stale item. Also enables
  // keyboard + Next/Prev navigation via index lookup on `visible`.
  const [previewProductId, setPreviewProductId] = useState(null);   // Phase Hub-UX.3.1
  // App Store 1.2 / 4.7 — open user reports surfaced inline with a badge
  // count so the moderation SLA stays visible on every admin visit.
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [reportsExpanded, setReportsExpanded] = useState(true);

  useEffect(() => {
    const off = getAuth().onAuthStateChanged((u) => setUser(u));
    return off;
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(getFirestore(), 'endProducts'));
      const list = snap.docs.map((d) => ({ ...d.data(), _id: d.id }));
      list.sort((a, b) => (b.publishedAt?.seconds || 0) - (a.publishedAt?.seconds || 0));
      setItems(list);
      return list;
    } catch (err) {
      setError(err.message || 'Failed to load endProducts');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!ADMIN_UIDS.includes(user.uid)) return;
    let cancelled = false;
    (async () => { if (!cancelled) await loadItems(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-point state (source project doc's `repoint`) lives on the project doc,
  // not the endProducts doc the table loads. Hydrate it lazily for the open
  // drawer item so the panel can show "Currently re-pointed → X" + Revert.
  const [repointStateById, setRepointStateById] = useState({});
  const repointHubUrlFn = useMemo(() => httpsCallable(getFunctions(undefined, 'us-central1'), 'repointHubUrl'), []);

  // Load open reports via Cloud Function. Uses the admin-gated callable so
  // the page works without each admin having the moderator custom claim set
  // (the function accepts ADMIN_UIDS allowlist as a fallback).
  const loadReports = async () => {
    if (!user || !ADMIN_UIDS.includes(user.uid)) return;
    setReportsLoading(true);
    setReportsError('');
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'listOpenHubReports');
      const res = await fn({});
      setReports(res.data?.reports || []);
    } catch (err) {
      setReportsError(err.message || 'Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  };

  // loadReports is re-created on every render, so depending on it would reload
  // the list on every render. Keyed on `user` deliberately. The disable has to
  // sit on its own line directly above the dep array — inline it lands on the
  // wrong line and the rule still fires.
  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const dismissReport = async (reportId, status) => {
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'resolveHubReport');
      await fn({ reportId, status });
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) {
      setReportsError(err.message || 'Failed to update report');
    }
  };

  const tagged = useMemo(() => items.map((p) => ({ ...p, _genre: genreOf(p) })), [items]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tagged.filter((p) => {
      if (removedOnly && p.removed !== true) return false;
      if (!removedOnly && !showRemoved && p.removed === true) return false;
      // Phase Hub-UX.3 — prompts have a dedicated /admin/prompts page with
      // richer per-applet UX. Hide them here by default so the hub list
      // stays focused on installable end-products.
      if (!showPrompts && p.contentCategory === 'prompt') return false;
      if (genreFilter && p._genre !== genreFilter) return false;
      if (q) {
        const hay = `${p.slug || ''} ${p.name || ''} ${p.title || ''} ${p.publisherName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tagged, query, genreFilter, showRemoved, removedOnly, showPrompts]);

  const catalogStatusCounts = useMemo(() => {
    const counts = { active: 0, removed: 0, all: 0 };
    for (const p of tagged) {
      if (!showPrompts && p.contentCategory === 'prompt') continue;
      counts.all += 1;
      if (p.removed === true) counts.removed += 1;
      else counts.active += 1;
    }
    return counts;
  }, [tagged, showPrompts]);

  const genreCounts = useMemo(() => {
    const ctr = {};
    for (const p of tagged) {
      if (removedOnly && p.removed !== true) continue;
      if (!removedOnly && p.removed === true && !showRemoved) continue;
      ctr[p._genre] = (ctr[p._genre] || 0) + 1;
    }
    return ctr;
  }, [tagged, showRemoved, removedOnly]);

  const relationCandidatesById = useMemo(() => {
    const out = new Map();
    const candidatePool = showRemoved ? tagged : tagged.filter((p) => p.removed !== true);
    for (const p of candidatePool) out.set(p._id, relatedCandidatesFor(p, candidatePool));
    return out;
  }, [tagged, showRemoved]);

  const relationRows = useMemo(() => {
    return visible
      .map((product) => ({
        product,
        candidates: relationCandidatesById.get(product._id) || [],
      }))
      .filter((row) => row.candidates.length > 0)
      .sort((a, b) => {
        const aa = a.candidates[0]?.score || 0;
        const bb = b.candidates[0]?.score || 0;
        return bb - aa || (a.product.name || a.product.slug || '').localeCompare(b.product.name || b.product.slug || '');
      });
  }, [visible, relationCandidatesById]);

  const relationStats = useMemo(() => {
    const stats = { 'exact-duplicate': 0, 'near-duplicate': 0, branch: 0, related: 0 };
    for (const row of relationRows) {
      const type = row.candidates[0]?.relationType || 'related';
      stats[type] = (stats[type] || 0) + 1;
    }
    return stats;
  }, [relationRows]);

  const toggleRemoved = async (p) => {
    const willRemove = !p.removed;
    const title = p.name || p.title || p.slug;
    if (willRemove) {
      const ok = window.confirm(`Remove "${title}" from the public Hub?\n\nThis is a soft delete — the URL /u/${p.publisherShortUid || ''}/${p.slug}/ keeps working. Only the /hub listing hides.`);
      if (!ok) return;
    } else {
      const ok = window.confirm(`Restore "${title}" to the public Hub?\n\nThis clears the soft-delete flag and marks the item approved + clean so /hub/${p.slug || p._id}/ can show again.`);
      if (!ok) return;
    }
    const update = willRemove
      ? {
          removed: true,
          removedAt: new Date(),
          removedBy: user?.uid || 'admin',
        }
      : {
          removed: false,
          removedAt: null,
          removedBy: null,
          securityReviewStatus: 'approved',
          moderationStatus: 'clean',
          moderationReviewedAt: serverTimestamp(),
          moderationReviewedBy: user?.uid || 'admin',
        };
    const localUpdate = willRemove
      ? update
      : {
          ...update,
          moderationReviewedAt: new Date(),
        };
    try {
      await updateDoc(doc(getFirestore(), 'endProducts', p._id), update);
      setError('');
      setItems((prev) => prev.map((x) => x._id === p._id ? { ...x, ...localUpdate } : x));
    } catch (err) {
      setError(err.message || (willRemove ? 'Remove failed' : 'Restore failed'));
    }
  };

  // Hydrate the open drawer item's re-point state from its project doc.
  useEffect(() => {
    if (!previewProductId) return;
    const p = items.find((x) => x._id === previewProductId);
    if (!p || !p.slug) return;
    const ownerUid = p.publisherUid || p.publisherShortUid;
    if (!ownerUid) return;
    if (repointStateById[previewProductId] !== undefined) return;   // already hydrated
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(getFirestore(), `users/${ownerUid}/projects/${p.slug}`));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : null;
        setRepointStateById((prev) => ({ ...prev, [previewProductId]: data?.repoint || null }));
      } catch (_) {
        // Best-effort (rules may block reading another user's project doc).
        if (!cancelled) setRepointStateById((prev) => ({ ...prev, [previewProductId]: null }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewProductId, items]);

  const doRepoint = async (sourceProduct, target, adopt) => {
    const res = await repointHubUrlFn({
      sourceSlug: sourceProduct.slug,
      sourceUid: sourceProduct.publisherUid || sourceProduct.publisherShortUid,
      targetSlug: target.slug,
      targetUid: target.uid,
      adoptIdentity: adopt,
    });
    if (!res?.data?.ok) throw new Error('Re-point failed');
    // Force re-hydration of this item's state + refresh mirrored identity.
    setRepointStateById((prev) => { const n = { ...prev }; delete n[sourceProduct._id]; return n; });
    await loadItems();
  };

  const doRevertRepoint = async (sourceProduct) => {
    const res = await repointHubUrlFn({
      sourceSlug: sourceProduct.slug,
      sourceUid: sourceProduct.publisherUid || sourceProduct.publisherShortUid,
      revert: true,
    });
    if (!res?.data?.ok) throw new Error('Revert failed');
    setRepointStateById((prev) => { const n = { ...prev }; delete n[sourceProduct._id]; return n; });
    await loadItems();
  };

  const setModerationStatus = async (p, moderationStatus) => {
    const update = {
      moderationStatus,
      moderationReviewedAt: serverTimestamp(),
      moderationReviewedBy: user?.uid || 'admin',
    };
    if (moderationStatus === 'clean') {
      update.securityReviewStatus = 'approved';
      update.securityFlags = [];
    }
    try {
      await updateDoc(doc(getFirestore(), 'endProducts', p._id), update);
      setItems((prev) => prev.map((x) => x._id === p._id ? { ...x, ...update, moderationReviewedAt: new Date() } : x));
    } catch (err) {
      setError(err.message || 'Moderation update failed');
    }
  };

  const catalogVisibilityView = removedOnly ? 'removed' : (showRemoved ? 'all' : 'active');
  const setCatalogVisibilityView = (view) => {
    if (view === 'removed') {
      setShowRemoved(true);
      setRemovedOnly(true);
    } else if (view === 'all') {
      setShowRemoved(true);
      setRemovedOnly(false);
    } else {
      setShowRemoved(false);
      setRemovedOnly(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>
        Sign in required. <Link to="/login">Sign in</Link>
      </div>
    );
  }
  if (!ADMIN_UIDS.includes(user.uid)) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#b91c1c' }}>
        Admin only.
      </div>
    );
  }

  return (
    <div style={{
      // Phase Hub-UX.3.2 — when the preview drawer is open it sits as a
      // fixed right rail. Shrink the page max-width and anchor it to the
      // left so the table columns (esp. the rightmost Action cell) stay
      // visible alongside the drawer. The right margin MUST match the
      // drawer's width (both use min(560px, 45vw)) plus a 24px gutter, or
      // the table's Action column slides under the drawer.
      maxWidth: previewProductId ? 'calc(100vw - min(560px, 45vw) - 48px)' : 1100,
      marginLeft: previewProductId ? 24 : 'auto',
      marginRight: previewProductId ? 'calc(min(560px, 45vw) + 24px)' : 'auto',
      padding: '40px 24px',
      transition: 'max-width 0.18s, margin-right 0.18s, margin-left 0.18s',
    }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        Hub Review & Catalog Cleanup
        {reports.length > 0 && (
          <span style={{
            background: '#dc2626', color: '#fff', fontSize: '0.75rem', fontWeight: 700,
            padding: '2px 10px', borderRadius: 999, lineHeight: 1.4,
          }}>
            {reports.length} open report{reports.length === 1 ? '' : 's'}
          </span>
        )}
      </h1>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: 24 }}>
        Approve pending submissions before they appear publicly, or soft-delete duplicates,
        QA artifacts, and stale variants from the public Hub. Use the Removed view to restore soft-deleted items.
      </p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem' }}>{error}</div>
      )}

      {/* Open user reports — Apple Guideline 1.2 / 4.7 moderation queue. */}
      {(reports.length > 0 || reportsError) && (
        <div style={{
          background: reports.length > 0 ? '#fffbeb' : '#fff',
          border: `1px solid ${reports.length > 0 ? '#fde68a' : '#e5e7eb'}`,
          borderRadius: 12, padding: 14, marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: reportsExpanded ? 12 : 0 }}>
            <strong style={{ color: '#92400e', fontSize: '0.95rem' }}>
              Pending user reports {reports.length > 0 ? `(${reports.length})` : ''}
            </strong>
            <button
              onClick={() => setReportsExpanded(!reportsExpanded)}
              style={{ background: 'none', border: 'none', color: '#92400e', fontSize: '0.8rem', cursor: 'pointer', padding: '2px 8px' }}>
              {reportsExpanded ? 'Collapse' : 'Expand'}
            </button>
            <button
              onClick={loadReports}
              disabled={reportsLoading}
              style={{ background: 'none', border: 'none', color: '#92400e', fontSize: '0.8rem', cursor: 'pointer', padding: '2px 8px' }}>
              {reportsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {reportsError && (
            <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: 8 }}>{reportsError}</div>
          )}
          {reportsExpanded && reports.map((r) => (
            <div key={r.id} style={{
              background: '#fff', border: '1px solid #fde68a', borderRadius: 8,
              padding: 10, marginBottom: 8, fontSize: '0.85rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    {r.productName || '(no name)'}{' '}
                    <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.78rem' }}>
                      / {r.productSlug || r.productId}
                    </span>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.78rem', marginBottom: 6 }}>
                    <strong style={{ color: '#dc2626' }}>{REPORT_CATEGORY_LABELS[r.category] || r.category}</strong>
                    {r.reporterEmail ? ` · reporter: ${r.reporterEmail}` : ''}
                  </div>
                  {r.reason && (
                    <div style={{ color: '#374151', whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>
                      {r.reason}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button
                    onClick={() => {
                      setQuery(r.productSlug || '');
                      window.scrollTo({ top: 320, behavior: 'smooth' });
                    }}
                    style={{
                      background: '#1f2025', color: '#fff', border: 'none',
                      padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer',
                    }}>
                    Find item
                  </button>
                  <button
                    onClick={() => dismissReport(r.id, 'dismissed')}
                    style={{
                      background: '#fff', color: '#374151', border: '1px solid #d1d5db',
                      padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer',
                    }}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search slug, title, or publisher..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.9rem' }}
        />
        <div
          role="group"
          aria-label="Catalog visibility"
          style={{
            display: 'flex',
            gap: 3,
            padding: 3,
            border: '1px solid #d1d5db',
            borderRadius: 999,
            background: '#f9fafb',
          }}>
          {[
            { key: 'active', label: 'Active', count: catalogStatusCounts.active },
            { key: 'removed', label: 'Removed', count: catalogStatusCounts.removed },
            { key: 'all', label: 'All', count: catalogStatusCounts.all },
          ].map((option) => {
            const selected = catalogVisibilityView === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setCatalogVisibilityView(option.key)}
                style={{
                  background: selected ? '#1f2025' : 'transparent',
                  color: selected ? '#fff' : '#374151',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: 999,
                  fontSize: '0.78rem',
                  fontWeight: selected ? 800 : 600,
                  cursor: 'pointer',
                }}>
                {option.label} ({option.count})
              </button>
            );
          })}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}
               title="Prompts have a dedicated /admin/prompts page; hidden here by default.">
          <input type="checkbox" checked={showPrompts} onChange={(e) => setShowPrompts(e.target.checked)} />
          Show prompts
        </label>
        <Link to="/admin/prompts" style={{ fontSize: '0.82rem', color: '#3b82f6', textDecoration: 'none' }}>
          → Prompt catalog
        </Link>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        <button
          onClick={() => setGenreFilter('')}
          style={{
            background: genreFilter === '' ? '#1f2025' : '#fff', color: genreFilter === '' ? '#fff' : '#374151',
            border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', cursor: 'pointer',
          }}>
          All ({Object.values(genreCounts).reduce((a, b) => a + b, 0)})
        </button>
        {Object.entries(genreCounts).sort((a, b) => b[1] - a[1]).map(([tag, n]) => (
          <button
            key={tag}
            onClick={() => setGenreFilter(tag)}
            style={{
              background: genreFilter === tag ? '#1f2025' : '#fff', color: genreFilter === tag ? '#fff' : '#374151',
              border: `1px solid ${tag === 'qa-junk' ? '#fecaca' : '#d1d5db'}`,
              padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', cursor: 'pointer',
            }}>
            {tag} ({n})
          </button>
        ))}
      </div>

      {!loading && relationRows.length > 0 && (
        <section style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 14,
          marginBottom: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: showRelations ? 12 : 0 }}>
	            <div>
	              <h2 style={{ margin: 0, color: '#111827', fontSize: '1rem', fontWeight: 800 }}>Relation Candidates</h2>
	              <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
	                {relationRows.length} item{relationRows.length === 1 ? '' : 's'} in this view · {relationStats['exact-duplicate'] || 0} exact · {relationStats['near-duplicate'] || 0} near duplicate · {relationStats.branch || 0} branch · {relationStats.related || 0} related
	              </div>
	              <div style={{ color: '#6b7280', fontSize: '0.72rem', marginTop: 3, lineHeight: 1.35 }}>
	                Exact duplicates require matching product DNA/source artifact plus matching title or slug identity. Near duplicates are similarity candidates. Hidden items are excluded unless Removed or All is selected.
	              </div>
	            </div>
            <button
              type="button"
              onClick={() => setShowRelations((value) => !value)}
              style={{
                background: '#f9fafb',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '5px 10px',
                borderRadius: 8,
                fontSize: '0.78rem',
                cursor: 'pointer',
              }}>
              {showRelations ? 'Hide' : 'Show'}
            </button>
          </div>
          {showRelations && (
	            <div style={{ display: 'grid', gap: 8 }}>
	              {relationRows.slice(0, 12).map(({ product, candidates }) => {
	                const top = candidates[0];
	                const meta = relationTypeMeta(top.relationType);
	                return (
                  <div key={product._id} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 1.4fr)',
                    gap: 10,
                    border: '1px solid #eef2f7',
                    borderRadius: 8,
                    padding: 10,
                    background: product.removed ? '#fef2f2' : '#f8fafc',
                  }}>
                    <button
                      type="button"
                      onClick={() => setPreviewProductId(product._id)}
                      style={{
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        minWidth: 0,
                      }}>
                      <div style={{ color: '#111827', fontSize: '0.84rem', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.name || product.title || product.slug}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.74rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.slug || product._id}
                      </div>
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <button
                        type="button"
                        onClick={() => setPreviewProductId(top._id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '7px 9px',
                          cursor: 'pointer',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
	                          <span style={{ color: '#111827', fontSize: '0.8rem', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
	                            {top.name || top.title || top.slug}
	                          </span>
	                          <span style={{ color: meta.tone, fontSize: '0.68rem', fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>
	                            {meta.label} · {top.score}
	                          </span>
                        </div>
                        {top.relationReasons?.length > 0 && (
                          <div style={{ color: '#4b5563', fontSize: '0.72rem', marginTop: 3, lineHeight: 1.35 }}>
                            {top.relationReasons.slice(0, 3).join(' · ')}
                          </div>
                        )}
                      </button>
                      {candidates.length > 1 && (
                        <div style={{ color: '#6b7280', fontSize: '0.7rem', marginTop: 3 }}>
                          +{candidates.length - 1} more candidate{candidates.length === 2 ? '' : 's'} in preview drawer
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>Loading endProducts...</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Title / Slug</th>
	                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Genre</th>
	                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Publisher</th>
	                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Plays</th>
	                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Relations</th>
	                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Review</th>
	                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>Action</th>
	              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const isRemoved = p.removed === true;
                const moderation = p.moderationStatus || 'missing';
                const security = p.securityReviewStatus || 'missing';
                // FIX (Phase Hub-UX.3): use the FULL publisherUid for the
                // /u/{uid}/{slug}/ path. The previous `.slice(0, 8)` truncation
                // generated 404 URLs.
	                const uid = p.publisherShortUid || p.publisherUid || null;
	                const url = uid && p.slug ? `https://mchatai.com/u/${uid}/${p.slug}/` : null;
		                const isActive = previewProductId === p._id;
		                const relationCandidates = relationCandidatesById.get(p._id) || [];
		                const topRelation = relationCandidates[0];
		                const topRelationMeta = topRelation ? relationTypeMeta(topRelation.relationType) : null;
		                return (
                  <tr key={p._id}
                      onClick={() => setPreviewProductId(p._id)}
                      style={{ borderTop: '1px solid #f3f4f6',
                               borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                               opacity: isRemoved ? 0.55 : 1,
                               background: isActive ? '#eef2ff' : (isRemoved ? '#fef2f2' : '#fff'),
                               cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontWeight: 600 }}>
                        {p.name || p.title || '(untitled)'}
                        {isRemoved && <span style={{ color: '#b91c1c', fontSize: '0.7rem', fontWeight: 700, marginLeft: 6 }}>HIDDEN</span>}
                        {moderation === 'pending' && <span style={{ color: '#d97706', fontSize: '0.7rem', fontWeight: 700, marginLeft: 6 }}>PENDING</span>}
                        {moderation === 'flagged' && <span style={{ color: '#b91c1c', fontSize: '0.7rem', fontWeight: 700, marginLeft: 6 }}>FLAGGED</span>}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{p.slug || p._id}</div>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#374151' }}>{p._genre}</td>
                    <td style={{ padding: '8px 12px', color: '#374151' }}>
                      {p.publisherName || (uid ? uid.slice(0, 8) + '…' : '?')}
	                    </td>
	                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>{p.totalPlays || 0}</td>
	                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
	                      {topRelation ? (
	                        <button
	                          type="button"
	                          onClick={(e) => {
	                            e.stopPropagation();
	                            setPreviewProductId(p._id);
	                          }}
		                          title={topRelation.relationReasons?.slice(0, 3).join(' · ') || 'Open relation candidates'}
		                          style={{
		                            background: topRelationMeta.bg,
		                            color: topRelationMeta.tone,
		                            border: `1px solid ${topRelationMeta.border}`,
		                            padding: '3px 8px',
		                            borderRadius: 999,
	                            fontSize: '0.7rem',
	                            fontWeight: 800,
	                            cursor: 'pointer',
		                            textTransform: 'uppercase',
		                          }}>
		                          {relationCandidates.length} {topRelationMeta.shortLabel}
		                        </button>
	                      ) : (
	                        <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>—</span>
	                      )}
	                    </td>
	                    <td style={{ padding: '8px 12px', color: '#374151' }}
	                        onClick={(e) => e.stopPropagation()}>
	                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 5 }}>
	                        moderation: <strong style={{ color: moderation === 'clean' ? '#15803d' : moderation === 'pending' ? '#d97706' : '#b91c1c' }}>{moderation}</strong>
	                        <br />
	                        security: <strong>{security}</strong>
	                      </div>
	                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
	                        {moderation !== 'clean' && (
	                          <button
	                            type="button"
	                            onClick={() => setModerationStatus(p, 'clean')}
                              title={isRemoved
                                ? 'Marks the review clean, but this item stays hidden until you also use Restore to Hub.'
                                : 'Marks this item approved + clean so it can appear on public Hub surfaces.'}
	                            style={{
	                              background: '#16a34a', color: '#fff', border: '1px solid #16a34a',
	                              padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
	                              fontSize: '0.7rem', fontWeight: 700,
	                            }}>
	                            Approve for Hub
	                          </button>
	                        )}
	                        {moderation !== 'pending' && (
	                          <button
	                            type="button"
	                            onClick={() => setModerationStatus(p, 'pending')}
                              title="Keeps this item out of public Hub surfaces while it waits for review."
	                            style={{
	                              background: '#fff', color: '#92400e', border: '1px solid #fde68a',
	                              padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
	                              fontSize: '0.7rem', fontWeight: 700,
	                            }}>
	                            Hold from Hub
	                          </button>
	                        )}
	                        {moderation !== 'flagged' && (
	                          <button
	                            type="button"
	                            onClick={() => setModerationStatus(p, 'flagged')}
                              title="Marks this item flagged and hides it from public Hub surfaces."
	                            style={{
	                              background: '#fff', color: '#b91c1c', border: '1px solid #fecaca',
	                              padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
	                              fontSize: '0.7rem', fontWeight: 700,
	                            }}>
	                            Flag / Hide
	                          </button>
	                        )}
	                      </div>
	                    </td>
	                    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}
	                        onClick={(e) => e.stopPropagation()}>
                      {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '0.78rem', textDecoration: 'none', marginRight: 8 }}>open ↗</a>
                      )}
                      <button
                        onClick={() => toggleRemoved(p)}
                        style={{
                          background: isRemoved ? '#16a34a' : 'transparent',
                          color: isRemoved ? '#fff' : '#b91c1c',
                          border: `1px solid ${isRemoved ? '#16a34a' : '#fecaca'}`,
                          padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.72rem', fontWeight: 600,
                        }}>
                        {isRemoved ? 'Restore to Hub' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
	                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No items match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 24, color: '#9ca3af', fontSize: '0.78rem' }}>
        Showing {visible.length} of {tagged.length} total. Public Hub surfaces require approved security review, clean moderation, and not removed. Removed items stay restorable from this page.
      </p>

      {(() => {
        if (!previewProductId) return null;
        const idx = visible.findIndex((p) => p._id === previewProductId);
        if (idx < 0) return null;   // filter excluded it; close drawer naturally
        const p = visible[idx];
        const uid = p.publisherShortUid || p.publisherUid || null;
        const url = uid && p.slug ? `https://mchatai.com/u/${uid}/${p.slug}/` : null;
        return (
          <PreviewModal
            product={{ ...p, _url: url, repoint: repointStateById[p._id] || null }}
            index={idx}
            total={visible.length}
            relationCandidates={relationCandidatesById.get(p._id) || []}
            onClose={() => setPreviewProductId(null)}
            onPrev={idx > 0 ? () => setPreviewProductId(visible[idx - 1]._id) : null}
            onNext={idx < visible.length - 1 ? () => setPreviewProductId(visible[idx + 1]._id) : null}
            onToggleRemoved={() => toggleRemoved(p)}
            onSelectCandidate={(candidate) => setPreviewProductId(candidate._id)}
            onRepoint={(target, adopt) => doRepoint(p, target, adopt)}
            onRevertRepoint={() => doRevertRepoint(p)}
          />
        );
      })()}
    </div>
  );
}
